import React, { useContext, useState } from "react";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import message from "antd/lib/message";
import Radio from "antd/lib/radio";
import Form from "antd/lib/form";
import { useMutation } from "react-query";
import { createApiClient } from "apiClient";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import {
  PatientContext,
  PatientContextState
} from "contextApis/patientContext";
import { UserContext, UserContextState } from "contextApis/userContext";
import { Platform, UserRoles } from "constants/roles";
import { FormSubmitButton } from "pages/symptoms/surveyForms/FormSubmitButton";
import { FormClearButton } from "pages/symptoms/surveyForms/FormClearButton";
import {
  SurveyPage,
  SurveyContent,
  SurveyHeader,
  SurveyNoticeCard,
  SurveyQuestionCard,
  AnswerGrid,
  AnswerList,
  AnswerOption,
  SurveyFooter,
  SurveyProgressBar,
  FooterActions,
  FormItemStyle
} from "pages/symptoms/surveyForms/surveyFormComponents";

interface ShortnessOfBreathFormProps {
  setShowSurveyForm(show: boolean): void;
  selectedItems: string[];
  goToNextFormOrReturn(): void;
}

export const ShortnessOfBreathForm: React.FC<ShortnessOfBreathFormProps> = ({
  setShowSurveyForm,
  selectedItems,
  goToNextFormOrReturn
}) => {
  const params = new URLSearchParams(window.location.search);
  const platform = params.get("platform");
  const [currentPage, setCurrentPage] = useState(1);

  const [form] = Form.useForm();
  const userContext = useContext<UserContextState>(UserContext);
  const patientContext = useContext<PatientContextState>(PatientContext);
  const [showSubmit, setShowSubmit] = useState(false);

  const userRole = sessionStorage.getItem("userRole");

  const {
    isLoading: isLoadingShortnessOfBreathMutation,
    mutate: submitShortnessOfBreathForm
  } = useMutation<string>(
    "add-bill",
    async () => {
      return await createApiClient().addShortnessOfBreathFormData({
        patient_internal_id:
          userRole === UserRoles.PATIENT && userContext.userData
            ? userContext.userData?.internal_id
            : patientContext?.patientData?.id || "",
        breath: localStorage.getItem("hasHadShortnessOfBreath") || "",
        level:
          localStorage.getItem("activitiesThatCauseShortnessOfBreath") || "",
        submitted_by: `${userContext.userData?.firstName} ${userContext.userData?.lastName}`
      });
    },
    {
      onSuccess: () => {
        message.success("Form Submitted Successfully");
        goToNextFormOrReturn();
      },
      onError: () => {
        message.error({
          content: (
            <span>
              There was a problem submitting this form, resubmit or{" "}
              <a onClick={() => goToNextFormOrReturn()}>
                click here to continue to the next survey
              </a>
            </span>
          ),
          style: {
            bottom: "10px",
            position: "fixed",
            left: "30%"
          },
          duration: 5
        });
      }
    }
  );

  const onFinish = () => {
    //Values to be passed on to API from session storage
    if (localStorage.getItem("hasHadShortnessOfBreath") === "No") {
      form.resetFields(["Activities that cause shortness of breath"]);
      localStorage.setItem("activitiesThatCauseShortnessOfBreath", "");
      submitShortnessOfBreathForm();
    } else if (currentPage === 1) {
      setCurrentPage(2);
    } else {
      submitShortnessOfBreathForm();
    }
  };

  const onClear = () => {
    if (currentPage === 1) {
      form.resetFields(["Any shortness of breath in last month"]);
    } else {
      form.resetFields(["Activities that cause shortness of breath"]);
    }
  };

  return (
    <SurveyPage>
      <div
        style={{
          marginBottom: "15px",
          display: platform === Platform.MOBILE ? "none" : "block"
        }}
      >
        <AppButton
          type={ButtonType.Primary}
          onClick={() => setShowSurveyForm(false)}
          buttonContent={
            <>
              <ArrowLeftOutlined /> Back
            </>
          }
        />
      </div>

      <SurveyContent>
        <Form
          name="shortnessOfBreath"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          style={{ width: "100%" }}
        >
          <SurveyHeader title={`Survey - ${selectedItems[0]}`} />

          {currentPage === 1 && (
            <>
              <SurveyNoticeCard />

              <SurveyQuestionCard
                title="Have you had any shortness of breath limiting your activity in the last one month?"
                required
              >
                <FormItemStyle
                  name="Any shortness of breath in last month"
                  label=""
                  rules={[{ required: true }]}
                  initialValue=""
                >
                  <Radio.Group
                    value={localStorage.getItem("hasHadShortnessOfBreath")}
                    onChange={e => {
                      if (e.target.value === "No") {
                        setShowSubmit(true);
                      } else {
                        setShowSubmit(false);
                      }
                      localStorage.setItem(
                        "hasHadShortnessOfBreath",
                        e.target.value
                      );
                    }}
                    style={{ width: "100%" }}
                  >
                    <AnswerGrid>
                      <AnswerOption>
                        <Radio value="Yes">Yes</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="No">No</Radio>
                      </AnswerOption>
                    </AnswerGrid>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>
            </>
          )}

          {currentPage === 2 && (
            <SurveyQuestionCard
              title="What activities cause shortness of breath?"
              required
            >
              <FormItemStyle
                name="Activities that cause shortness of breath"
                label=""
                rules={[{ required: true }]}
                initialValue=""
              >
                <Radio.Group
                  value={localStorage.getItem(
                    "activitiesThatCauseShortnessOfBreath"
                  )}
                  onChange={e =>
                    localStorage.setItem(
                      "activitiesThatCauseShortnessOfBreath",
                      e.target.value
                    )
                  }
                  style={{ width: "100%" }}
                >
                  <AnswerList>
                    <AnswerOption>
                      <Radio
                        value={
                          "Only vigorous activities (No limitation of normal activity)"
                        }
                      >
                        Only vigorous activities (No limitation of normal
                        activity)
                      </Radio>
                    </AnswerOption>
                    <AnswerOption>
                      <Radio
                        value={
                          "Walking briskly, or on incline (Slight limitation of ordinary activity)"
                        }
                      >
                        Walking briskly, or on incline (Slight limitation of
                        ordinary activity)
                      </Radio>
                    </AnswerOption>
                    <AnswerOption>
                      <Radio
                        value={
                          "Walking 20 - 100 feet on the level (Moderate limitation of LESS-THAN-ordinary activity)"
                        }
                      >
                        Walking 20 - 100 feet on the level (Moderate limitation
                        of LESS-THAN-ordinary activity)
                      </Radio>
                    </AnswerOption>
                    <AnswerOption>
                      <Radio
                        value={
                          "At rest or on minimal activity (E.g. dressing, getting out of bed)"
                        }
                      >
                        At rest or on minimal activity (E.g. dressing, getting
                        out of bed)
                      </Radio>
                    </AnswerOption>
                  </AnswerList>
                </Radio.Group>
              </FormItemStyle>
            </SurveyQuestionCard>
          )}

          <SurveyFooter>
            <SurveyProgressBar currentPage={currentPage} totalPages={2} />

            <FooterActions>
              <Form.Item>
                <FormClearButton onClear={onClear} />
              </Form.Item>

              {currentPage !== 1 && (
                <AppButton
                  type={ButtonType.Primary}
                  buttonContent={
                    <>
                      <ArrowLeftOutlined /> Back
                    </>
                  }
                  onClick={() => setCurrentPage(1)}
                  style={{ width: "105px" }}
                />
              )}

              <Form.Item>
                <FormSubmitButton
                  type={ButtonType.Primary}
                  buttonContent={
                    showSubmit || currentPage !== 1 ? (
                      "Submit"
                    ) : (
                      <>
                        Next <ArrowRightOutlined />
                      </>
                    )
                  }
                  htmlType="submit"
                  loading={isLoadingShortnessOfBreathMutation}
                />
              </Form.Item>
            </FooterActions>
          </SurveyFooter>
        </Form>
      </SurveyContent>
    </SurveyPage>
  );
};
